// P1-6 / P1-20 / P1-21: who is allowed to do what, and what counts as a valid password.
//
// ── WHY THIS EXISTS ──────────────────────────────────────────────────────────
// Role checks here are scattered and inconsistent: inline `[1,2,3]` literals in
// AssetManagement.js and CollectionReports.js, two more arrays in roles.js, and
// `role > MANAGER_ROLE_MAX` on the server. The security review logs that as M-6.
// Consolidating what already exists is not in scope for these items, but every NEW
// check goes here rather than adding to the sprawl.
//
// These rules also have to be separable from their wiring to be testable at all.
// There are no component tests in this package -- every test under src/ is a pure
// utilities test -- so a role check written inline in AssetManagement.js is a role
// check nothing verifies. The rule is extracted, the rule is tested, and the call
// site stays a one-liner that can be read by eye.
//
// ⚠ MUST AGREE WITH CC_Backend/utils/permissions.js. Same rules, two packages: CRA's
// ModuleScopePlugin forbids importing from outside src/, and the backend deploy
// artifact is CC_Backend only. The two test files share one case table and say so.
// The server is what enforces these; this copy exists so the UI offers exactly what
// the server will allow. If they drift, the app either offers an action that then
// fails, or hides one the server is quietly still permitting.

// ── THE ROLE LADDER ──────────────────────────────────────────────────────────
//
// ⚠ LOWER ROLE ID MEANS HIGHER PRIVILEGE. This is the single most counter-intuitive
// fact in the codebase and the reason P1-20 needs a guard at all:
//
//   1 System Admin   2 Admin   3 Manager   4 Collector   5 User   6 Power User
//
// Manager-or-above is ids 1-3, matching the server's `role > MANAGER_ROLE_MAX`
// rejection. Ids 5 and 6 exist in the live database (7 and 2 accounts as of
// 2026-08-24) and in roles.js, but are NOT creatable through the API, which accepts
// only 1-4. That mismatch is M-6's territory, not ours; what matters here is that 5
// and 6 are below manager and get nothing -- including Power User, which despite
// appearing in `customerManagementEditorRoles` is not a management role.
const MANAGER_ROLE_MAX = 3;

// Which roles may be ASSIGNED when creating a user. Narrower than the ladder above:
// 5 and 6 exist in the live data but nothing knows what they are for, so nothing new
// should be created with them.
//
// P1-21 moved this out of the server's data/users.js (where it was `VALID_ROLE_IDS`,
// enforced there and matched by nothing here) so the New User dialog offers exactly
// the set the server accepts. The dialog offered no role at all before, which is why
// every creation attempt was rejected.
const ASSIGNABLE_ROLE_IDS = [1, 2, 3, 4];

// Matches the rule user creation has always enforced on the server, which now
// imports it from its own copy of this file so there is one definition per package.
// M-2 is that /update-password had no length rule at all, so the minimum enforced at
// creation could be undone from the Profile page.
const MIN_PASSWORD_LENGTH = 8;

// Machine-readable refusal codes the server returns, following the
// BLIND_COLLECTION / MODE_LOCKED precedent in scanMode.js: this app switches on
// `result.code` rather than matching message text.
const MANUAL_ADD_FORBIDDEN = 'MANUAL_ADD_FORBIDDEN';
const PASSWORD_RESET_FORBIDDEN = 'PASSWORD_RESET_FORBIDDEN';
const WEAK_PASSWORD = 'WEAK_PASSWORD';
const CURRENT_PASSWORD_INVALID = 'CURRENT_PASSWORD_INVALID';

// A role id arrives from a decoded JWT claim (a number) or from a Select's value (a
// string -- the role dropdown's keys come from Object.entries, so they are strings).
// Coerce once, here, and return null for anything that is not a whole positive id so
// every predicate below can fail closed on it.
//
// `role >= 1` matters: without it a role of 0 or -1 would read as MORE privileged
// than a system admin, which is the direction that costs something. The type check
// matters for the same reason -- `Number(true)` is 1, so without it a claim carrying
// `role: true` would read as a system admin.
const roleId = (role) => {
    if (typeof role !== 'number' && typeof role !== 'string') return null;
    if (String(role).trim() === '') return null;
    const id = Number(role);
    return Number.isInteger(id) && id >= 1 ? id : null;
};

const isManagerOrAbove = (role) => {
    const id = roleId(role);
    return id != null && id <= MANAGER_ROLE_MAX;
};

const isAssignableRole = (role) => {
    const id = roleId(role);
    return id != null && ASSIGNABLE_ROLE_IDS.includes(id);
};

// ── P1-6: MAY THIS USER ADD AN UNEXPECTED ASSET BY HAND? ─────────────────────
//
// CompuCycle's 07/29/2026 call: onsite drivers must not be able to add unexpected
// assets to the intent list. Drives whether the "Add Unexpected Asset" button
// renders at all. An alias of isManagerOrAbove, named for the decision it drives so
// the call site reads as what it does -- the same reason removeDeletesRow exists in
// scanMode.js.
//
// ⚠ The button is DUAL-PURPOSE: the same element is "Edit Asset" once a row is
// selected (newAssetId != null). Gating the whole element would take editing away
// too, which nobody asked for. The call site tests this only on the add path.
const canAddUnexpectedAsset = (role) => isManagerOrAbove(role);

// ⚠ THE TRAP IN P1-6. `POST /assets` serves TWO actions, and only one is gated:
//
//   the "Add Unexpected Asset" button -> collected: false, no scan identity
//   any scan resolving UNEXPECTED     -> collected: true + scan_uid/device_scanned_at
//
// (See saveNewAsset: `...(collected ? takeScanIdentity() : {})`.) So the route itself
// must NOT be gated on the server. That would stop a Collector recording an
// unexpected find on an interactive order and stop them scanning AT ALL on a blind
// order, where every scan is a create -- undoing P1-5 for exactly the people it was
// built for. The gate is inside the handler, on the payload shape, and this is the
// predicate that reads it.
//
// Exported here for the same reason scanMode.js exports unexpectedFlagFor: so this
// package can predict what the server will do with a payload, and so the rule the
// server enforces is covered by the shared case table. Nothing in the UI calls it at
// runtime -- the UI decides by hiding the button.
//
// Note the formula: this is `NOT (a scan)`, not "uncollected AND no uid". The two
// differ for shapes the client never sends, and the difference is deliberate. A scan
// is precisely `collected: true` WITH an identity; anything else is a manual add and
// needs the role, so an unrecognised payload fails closed. A real scan always carries
// a non-empty uid -- buildScanUid falls back to 'NOLOT' rather than an empty string.
const isManualAssetAdd = (body) => {
    if (body == null) return true;
    const scanUid = body.scan_uid;
    const hasScanIdentity = typeof scanUid === 'string' && scanUid.trim() !== '';
    return !(body.collected === true && hasScanIdentity);
};

// ── P1-20: MAY THIS USER RESET THAT USER'S PASSWORD? ─────────────────────────
//
// ⚠ THE POINT OF THE ITEM. Because lower id means higher privilege, an unguarded
// admin reset lets a Manager (3) set a System Admin's (1) password and then log in as
// them. There are six live system admin accounts. The rule is therefore not "are you
// an admin" but "are you an admin, and is the target no more privileged than you".
//
// Decided 2026-08-24:
//
//   PEERS ARE ALLOWED (targetRole >= actorRole, not >). Nothing outranks role 1, so a
//   strictly-lower rule would leave the six system admins with no recovery route at
//   all -- and recovery is the entire reason P1-20 exists, since `users` has no email
//   column and self-service is impossible once you are locked out.
//
//   SELF IS EXCLUDED. Changing your own password goes through /update-password, which
//   after P1-20 demands the current one. Without this clause a Manager+ could route
//   around that check with their own token, leaving M-2 open for precisely the
//   accounts where it matters most. The Profile page is where you change your own.
//
// Takes an object rather than four positional arguments: two role ids and two user
// ids next to each other is exactly the signature where a call site silently swaps a
// pair, and here that would mean reading the guard backwards.
const canResetPassword = ({ actorRole, actorId, targetRole, targetId } = {}) => {
    if (!isManagerOrAbove(actorRole)) return false;

    const actor = roleId(actorRole);
    const target = roleId(targetRole);
    // An unknown target role cannot be compared, so it cannot be cleared.
    if (target == null) return false;
    // Upward is refused; equal is allowed (see above).
    if (target < actor) return false;

    // Self-reset belongs to /update-password. Ids are compared numerically for the
    // same reason roles are -- a grid row's id and a JWT claim need not be the same
    // type.
    const actorUser = Number(actorId);
    const targetUser = Number(targetId);
    if (Number.isFinite(actorUser) && Number.isFinite(targetUser) && actorUser === targetUser) {
        return false;
    }

    return true;
};

// ── THE ONE PASSWORD RULE ────────────────────────────────────────────────────
//
// Three call sites share it: user creation (P1-21's new dialog fields), the
// self-service change on the Profile page, and the admin reset. Returns the problem
// rather than a boolean so all three can say the same thing to the user instead of
// inventing three messages -- and so the message the client shows before sending
// matches the one the server would have sent back.
const passwordProblem = (password) => {
    if (password == null || String(password) === '') {
        return 'password is required';
    }
    if (String(password).length < MIN_PASSWORD_LENGTH) {
        return `password must be at least ${MIN_PASSWORD_LENGTH} characters`;
    }
    return null;
};

const isAcceptablePassword = (password) => passwordProblem(password) === null;

const exports = {
    MANAGER_ROLE_MAX,
    ASSIGNABLE_ROLE_IDS,
    MIN_PASSWORD_LENGTH,
    MANUAL_ADD_FORBIDDEN,
    PASSWORD_RESET_FORBIDDEN,
    WEAK_PASSWORD,
    CURRENT_PASSWORD_INVALID,
    roleId,
    isManagerOrAbove,
    isAssignableRole,
    canAddUnexpectedAsset,
    isManualAssetAdd,
    canResetPassword,
    passwordProblem,
    isAcceptablePassword,
};

export default exports;
export {
    MANAGER_ROLE_MAX,
    ASSIGNABLE_ROLE_IDS,
    MIN_PASSWORD_LENGTH,
    MANUAL_ADD_FORBIDDEN,
    PASSWORD_RESET_FORBIDDEN,
    WEAK_PASSWORD,
    CURRENT_PASSWORD_INVALID,
    roleId,
    isManagerOrAbove,
    isAssignableRole,
    canAddUnexpectedAsset,
    isManualAssetAdd,
    canResetPassword,
    passwordProblem,
    isAcceptablePassword,
};
